<template>
    <LoadingComponent :props="loading"/>

    <div class="db-card db-tab-div active">
        <!-- BANNER DE ALERTA DE ERROR EN SINCRONIZACIÓN API -->
        <div v-if="apiForm.currency_last_update_status === 'error'" class="m-4 p-4 rounded-xl border border-red-200 bg-red-50 flex items-start gap-3.5 text-red-800 shadow-xs">
            <i class="fa-solid fa-triangle-exclamation text-xl text-red-600 mt-0.5 flex-shrink-0"></i>
            <div class="flex-1 text-sm">
                <h4 class="font-bold text-red-900 mb-0.5">⚠️ Atención: Incidencia en la última sincronización de Tipo de Cambio</h4>
                <p class="mb-1.5 text-red-700 font-medium text-xs leading-relaxed">{{ apiForm.currency_last_update_message }}</p>
                <span class="text-[11px] text-red-500 font-semibold block">Último intento: {{ apiForm.currency_last_updated_at }}</span>
            </div>
            <button
                type="button"
                class="px-3.5 py-1.5 bg-red-600 text-white rounded-lg text-xs font-semibold hover:bg-red-700 transition flex items-center gap-1.5 flex-shrink-0 shadow-sm"
                @click="syncExchangeRates"
                :disabled="isSyncing"
            >
                <i class="fa-solid fa-rotate-right" :class="{ 'animate-spin': isSyncing }"></i>
                <span>{{ isSyncing ? ($t('button.syncing') || 'Sincronizando...') : 'Reintentar Ahora' }}</span>
            </button>
        </div>

        <div class="db-card-header flex items-center justify-between p-2 pl-4 border-b">
            <h3 class="db-card-title pt-1">{{ $t("menu.currencies") }}</h3>
            
            <div class="flex items-center gap-3">
                <!-- CONTROLES Y FILTROS TAB 1 -->
                <div v-if="activeTab === 'list'" class="db-card-filter flex items-center gap-2">
                    <button
                        type="button"
                        class="db-btn h-[37px] py-2 px-3 text-white bg-success flex items-center gap-1.5"
                        @click="syncExchangeRates"
                        :disabled="isSyncing"
                        :class="{ 'disabled:opacity-50 disabled:bg-gray': isSyncing }"
                        :title="$t('button.sync_rates_now') || 'Sincronizar tipos de cambio ahora'"
                    >
                        <template v-if="isSyncing">
                            <svg class="inline-block animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                            </svg>
                            <span>{{ $t('button.syncing') || 'Sincronizando...' }}</span>
                        </template>
                        <template v-else>
                            <i class="fa-solid fa-cloud-arrow-down"></i>
                            <span>{{ $t('button.sync_now') || 'Sincronizar Tasas' }}</span>
                        </template>
                    </button>

                    <TableLimitComponent :method="list" :search="props.search" :page="paginationPage"/>
                    <FilterComponent v-if="paginationPage && paginationPage.total > Number(props.search.per_page || 10)" :page="pagination" :search="props.search" @click.prevent="handleSlide('currency-filter')"/>
                    <CurrencyCreateComponent :props="props"/>
                
                    <button
                        type="button"
                        class="db-btn h-[37px] py-2 px-3 text-white bg-primary"
                        @click="refreshFromServer"
                        :disabled="isRefreshingView"
                        :class="{ 'disabled:opacity-50 disabled:bg-gray': isRefreshingView }"
                    >
                        <template v-if="isRefreshingView">
                            <svg class="inline-block animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                            </svg>
                        </template>
                        <template v-else>
                            <i class="fa-solid fa-arrows-rotate"></i>
                        </template>
                    </button>
                </div>
                <!-- TABS SWITCHER -->
                <div class="inline-flex rounded-lg bg-gray-100 p-1 gap-1">
                    <button
                        type="button"
                        class="px-4 h-9 rounded-md text-sm font-medium transition"
                        :class="activeTab === 'list' ? 'bg-white shadow-sm text-primary' : 'text-gray-600 hover:text-primary'"
                        @click="activeTab = 'list'"
                    >
                        <i class="fa-solid fa-coins mr-1.5"></i>
                        {{ $t('label.currencies_list') || 'Lista de Monedas' }}
                    </button>
                    <button
                        type="button"
                        class="px-4 h-9 rounded-md text-sm font-medium transition"
                        :class="activeTab === 'api_config' ? 'bg-white shadow-sm text-primary' : 'text-gray-600 hover:text-primary'"
                        @click="activeTab = 'api_config'"
                    >
                        <i class="fa-solid fa-arrows-rotate mr-1.5"></i>
                        {{ $t('label.auto_sync_api') || 'Sincronización API' }}
                    </button>
                </div>
            </div>
        </div>

        <!-- TAB 1: LISTADO DE MONEDAS -->
        <div v-if="activeTab === 'list'">
            <div v-if="paginationPage && paginationPage.total > Number(props.search.per_page || 10)" class="table-filter-div" id="currency-filter">
                <form class="p-4 sm:p-5 border-t" @submit.prevent="search">
                    <div class="row pt-2">
                        <div class="col-12 sm:col-6 md:col-4 xl:col-3 !pt-0">
                            <label for="searchCurrencyName" class="db-field-title after:hidden">{{ $t("label.name") }}</label>
                            <input id="searchCurrencyName" v-model="props.search.name" type="text" class="db-field-control"/>
                        </div>

                        <div class="col-12 border-t mt-1">
                            <div class="flex flex-wrap justify-end gap-2">
                                <button class="db-btn py-2 px-3 bg-gray-200" @click.prevent="handleSlide('currency-filter')">
                                    <i class="lab lab-fill-close"></i>
                                </button>
                                <button class="db-btn py-2 bg-gray-200" @click="clear">
                                    <span>{{ $t('button.restore') }}</span>
                                </button>
                                <button class="db-btn py-2 text-white bg-primary">
                                    <span>{{ $t('button.apply_filter') }}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>

            <div class="db-table-responsive">
                <table class="db-table stripe">
                    <thead class="db-table-head">
                    <tr class="db-table-head-tr">
                        <th class="db-table-head-th">{{ $t("label.name") }}</th>
                        <th class="db-table-head-th">
                            {{ $t("label.symbol") }}
                        </th>
                        <th class="db-table-head-th">
                            {{ $t("label.code") }}
                        </th>
                        <th class="db-table-head-th">
                            {{ $t("label.is_cryptocurrency") }}
                        </th>
                        <th class="db-table-head-th">
                            {{ $t("label.exchange_rate") }}
                        </th>
                        <th class="db-table-head-th text-right">
                            {{ $t("label.action") }}
                        </th>
                    </tr>
                    </thead>
                    <tbody class="db-table-body" v-if="currencies.length > 0">
                    <tr class="db-table-body-tr" v-for="currency in currencies" :key="currency.id">
                        <td class="db-table-body-td" v-if="site_default_currency === currency.id">
                            <span class="font-semibold text-primary">{{ currency.name }}</span> ({{ $t('label.default') }})
                        </td>
                        <td class="db-table-body-td" v-else>
                            {{ currency.name }}
                        </td>
                        <td class="db-table-body-td">
                            {{ currency.symbol }}
                        </td>
                        <td class="db-table-body-td font-mono font-semibold">
                            {{ currency.code }}
                        </td>
                        <td class="db-table-body-td">
                                <span :class="askClass(currency.is_cryptocurrency)">
                                    {{ enums.askEnumArray[currency.is_cryptocurrency] }}
                                </span>
                        </td>
                        <td class="db-table-body-td font-mono">
                            {{ currency.exchange_rate }}
                        </td>

                        <td class="db-table-body-td">
                            <div class="flex justify-end items-center sm:items-start sm:justify-end gap-1.5">
                                <SmModalEditComponent @click="edit(currency)"/>
                                <SmDeleteComponent @click="destroy(currency.id)"
                                                   v-if="site_default_currency != currency.id"/>
                            </div>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <div class="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-6">
                <PaginationSMBox :pagination="pagination" :method="list"/>
                <div class="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                    <PaginationTextComponent :props="{ page: paginationPage }"/>
                    <PaginationBox :pagination="pagination" :method="list"/>
                </div>
            </div>
        </div>

        <!-- TAB 2: CONFIGURACIÓN DE SINCRONIZACIÓN API (FRANKFURTER) -->
        <div v-else-if="activeTab === 'api_config'" class="db-card-body p-6">
            <form @submit.prevent="saveApiSettings">
                <div class="form-row">
                    <div class="form-col-12 border-b pb-3 mb-4">
                        <h4 class="font-client font-medium text-base text-gray-800 flex items-center gap-2">
                            <i class="fa-solid fa-globe text-primary"></i>
                            <span>{{ $t('label.frankfurter_api_integration') || 'Sincronización Automática con Frankfurter API' }}</span>
                        </h4>
                        <p class="text-xs text-gray-500 mt-1">
                            {{ $t('label.frankfurter_api_desc') || 'Actualiza automáticamente las tasas de cambio de las monedas secundarias tomando como base la moneda predeterminada del sistema.' }}
                        </p>
                    </div>

                    <!-- ACTIVAR / DESACTIVAR SINCRONIZACIÓN -->
                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title required">{{ $t('label.auto_update_status') || 'Actualización Automática' }}</label>
                        <div class="db-field-radio-group">
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input :value="1" v-model.number="apiForm.currency_auto_update_enabled" id="auto_update_enable" type="radio" class="custom-radio-field" />
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="auto_update_enable" class="db-field-label">{{ $t('label.enable') }}</label>
                            </div>
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input :value="0" v-model.number="apiForm.currency_auto_update_enabled" id="auto_update_disable" type="radio" class="custom-radio-field" />
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="auto_update_disable" class="db-field-label">{{ $t('label.disable') }}</label>
                            </div>
                        </div>
                    </div>

                    <!-- FRECUENCIA DE ACTUALIZACIÓN -->
                    <div class="form-col-12 sm:form-col-6">
                        <label for="currency_update_frequency" class="db-field-title required">{{ $t('label.update_frequency') || 'Frecuencia de Actualización' }}</label>
                        <select id="currency_update_frequency" v-model="apiForm.currency_update_frequency" class="db-field-control">
                            <option value="daily">{{ $t('label.frequency_daily') || 'Diaria (Recomendado)' }}</option>
                            <option value="12h">{{ $t('label.frequency_12h') || 'Cada 12 horas' }}</option>
                            <option value="6h">{{ $t('label.frequency_6h') || 'Cada 6 horas' }}</option>
                        </select>
                    </div>

                    <!-- MARGEN DE SEGURIDAD (SPREAD BUFFER %) -->
                    <div class="form-col-12 sm:form-col-6 mt-3">
                        <label for="currency_spread_buffer_percentage" class="db-field-title required">
                            {{ $t('label.spread_buffer_percentage') || 'Margen de Seguridad (%)' }}
                            <span class="text-xs text-primary font-normal ml-1">({{ $t('label.recommended') || 'Default: 1.5%' }})</span>
                        </label>
                        <div class="relative">
                            <input
                                id="currency_spread_buffer_percentage"
                                type="number"
                                step="0.01"
                                min="0"
                                max="50"
                                v-model.number="apiForm.currency_spread_buffer_percentage"
                                class="db-field-control pr-8"
                                placeholder="1.50"
                            />
                            <span class="absolute right-3 top-2.5 text-gray-500 font-bold">%</span>
                        </div>
                        <p class="text-[11px] text-gray-500 mt-1">
                            {{ $t('label.spread_buffer_help') || 'Se suma como porcentaje adicional sobre la tasa oficial para amortiguar comisiones bancarias o fluctuaciones cambiarias.' }}
                        </p>
                    </div>

                    <!-- ESTADO DE LA ÚLTIMA SINCRONIZACIÓN -->
                    <div class="form-col-12 border-t pt-4 mt-6">
                        <h5 class="font-medium text-sm text-gray-700 mb-2">{{ $t('label.last_sync_info') || 'Estado de la última sincronización' }}</h5>
                        
                        <div class="p-4 rounded-lg border bg-gray-50 flex flex-col gap-2">
                            <div class="flex items-center justify-between text-xs text-gray-600">
                                <span><strong>{{ $t('label.last_sync_date') || 'Última fecha' }}:</strong> {{ apiForm.currency_last_updated_at || 'Nunca' }}</span>
                                <div>
                                    <span v-if="apiForm.currency_last_update_status === 'success'" class="px-2 py-0.5 rounded text-white bg-success font-medium">
                                        <i class="fa-solid fa-check mr-1"></i> {{ $t('label.success') || 'Exitoso' }}
                                    </span>
                                    <span v-else-if="apiForm.currency_last_update_status === 'error'" class="px-2 py-0.5 rounded text-white bg-danger font-medium">
                                        <i class="fa-solid fa-circle-exclamation mr-1"></i> {{ $t('label.error') || 'Error' }}
                                    </span>
                                    <span v-else class="px-2 py-0.5 rounded text-gray-700 bg-gray-200">
                                        {{ $t('label.pending') || 'Sin ejecutar' }}
                                    </span>
                                </div>
                            </div>
                            
                            <p v-if="apiForm.currency_last_update_message" class="text-xs font-mono text-gray-700 bg-white p-2 rounded border">
                                {{ apiForm.currency_last_update_message }}
                            </p>
                        </div>
                    </div>

                    <!-- BOTONES DE ACCIÓN -->
                    <div class="form-col-12 border-t pt-5 mt-4 flex items-center justify-between">
                        <button type="submit" class="db-btn text-white bg-primary flex items-center gap-2" :disabled="isSavingApi">
                            <template v-if="isSavingApi">
                                <svg class="inline-block animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                    <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                </svg>
                                <span>{{ $t('button.saving') || 'Guardando...' }}</span>
                            </template>
                            <template v-else>
                                <i class="fa-solid fa-floppy-disk"></i>
                                <span>{{ $t('button.save_settings') || 'Guardar Configuración API' }}</span>
                            </template>
                        </button>

                        <button
                            type="button"
                            class="db-btn text-white bg-success flex items-center gap-2"
                            @click="syncExchangeRates"
                            :disabled="isSyncing"
                            :class="{ 'disabled:opacity-50 disabled:bg-gray': isSyncing }"
                        >
                            <template v-if="isSyncing">
                                <svg class="inline-block animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                    <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                </svg>
                                <span>{{ $t('button.syncing') || 'Sincronizando...' }}</span>
                            </template>
                            <template v-else>
                                <i class="fa-solid fa-cloud-arrow-down"></i>
                                <span>{{ $t('button.sync_now') || 'Sincronizar Tasas Ahora' }}</span>
                            </template>
                        </button>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import axios from "axios";
import LoadingComponent from "../../components/LoadingComponent";
import CurrencyCreateComponent from "./CurrencyCreateComponent";
import alertService from "../../../../services/alertService";
import PaginationTextComponent from "../../components/pagination/PaginationTextComponent";
import PaginationBox from "../../components/pagination/PaginationBox";
import PaginationSMBox from "../../components/pagination/PaginationSMBox";
import appService from "../../../../services/appService";
import TableLimitComponent from "../../components/TableLimitComponent";
import SmDeleteComponent from "../../components/buttons/SmDeleteComponent";
import SmModalEditComponent from "../../components/buttons/SmModalEditComponent";
import askEnum from "../../../../enums/modules/askEnum";
import FilterComponent from "../../components/buttons/collapse/FilterComponent";

export default {
    name: "CurrencyListComponent",
    components: {
        TableLimitComponent,
        PaginationSMBox,
        PaginationBox,
        PaginationTextComponent,
        CurrencyCreateComponent,
        LoadingComponent,
        SmDeleteComponent,
        SmModalEditComponent,
        askEnum,
        FilterComponent,
    },
    data() {
        return {
            activeTab: 'list',
            isRefreshingView: false,
            isSyncing: false,
            isSavingApi: false,
            loading: {
                isActive: false,
            },
            enums: {
                askEnum: askEnum,
                askEnumArray: {
                    [askEnum.YES]: this.$t("label.yes"),
                    [askEnum.NO]: this.$t("label.no"),
                },
            },
            props: {
                form: {
                    name: "",
                    symbol: "",
                    code: "",
                    is_cryptocurrency: askEnum.NO,
                    exchange_rate: "",
                },
                search: {
                    paginate: 1,
                    page: 1,
                    per_page: 10,
                    order_column: "id",
                    order_type: "desc",
                    name: "",
                },
            },
            apiForm: {
                currency_auto_update_enabled: 1,
                currency_update_frequency: 'daily',
                currency_spread_buffer_percentage: 1.50,
                currency_last_updated_at: null,
                currency_last_update_status: null,
                currency_last_update_message: null,
            },
            site_default_currency: null,
        };
    },
    mounted() {
        this.list();
        this.siteList();
        this.fetchApiSettings();
    },
    computed: {
        currencies: function () {
            return this.$store.getters["currency/lists"];
        },
        pagination: function () {
            return this.$store.getters["currency/pagination"];
        },
        paginationPage: function () {
            return this.$store.getters["currency/page"];
        },
    },
    methods: {
        fetchApiSettings: function () {
            axios.get('admin/setting/currency-exchange').then(res => {
                if (res.data && res.data.data) {
                    this.apiForm = { ...this.apiForm, ...res.data.data };
                }
            }).catch(err => {
                console.error("Error fetching currency exchange settings", err);
            });
        },
        saveApiSettings: function () {
            this.isSavingApi = true;
            axios.put('admin/setting/currency-exchange', {
                currency_auto_update_enabled: this.apiForm.currency_auto_update_enabled,
                currency_update_frequency: this.apiForm.currency_update_frequency,
                currency_spread_buffer_percentage: this.apiForm.currency_spread_buffer_percentage,
            }).then(res => {
                this.isSavingApi = false;
                alertService.success(res.data.message || this.$t('message.update_success'));
                this.fetchApiSettings();
            }).catch(err => {
                this.isSavingApi = false;
                if (err.response && err.response.data && err.response.data.message) {
                    alertService.error(err.response.data.message);
                } else {
                    alertService.error("Error guardando configuración API.");
                }
            });
        },
        syncExchangeRates: function () {
            if (this.isSyncing) return;
            this.isSyncing = true;
            axios.post('admin/setting/currency-exchange/sync').then(res => {
                this.isSyncing = false;
                alertService.success(res.data.message || "Sincronización exitosa.");
                this.list();
                this.fetchApiSettings();
            }).catch(err => {
                this.isSyncing = false;
                if (err.response && err.response.data && err.response.data.message) {
                    alertService.error(err.response.data.message);
                } else {
                    alertService.error("Error sincronizando tasas con Frankfurter API.");
                }
            });
        },
        refreshFromServer: function () {
            if (this.isRefreshingView) {
                return;
            }
            this.isRefreshingView = true;
            this.list().then(() => {
                alertService.info(this.$t('button.refresh'));
                this.isRefreshingView = false;
            }).catch(() => {
                this.isRefreshingView = false;
            });
        },
        askClass: function (ask) {
            return appService.askClass(ask);
        },
        textShortener: function (text, number = 30) {
            return appService.textShortener(text, number);
        },
        handleSlide: function (id) {
            return appService.handleSlide(id);
        },
        search: function () {
            this.list();
        },
        clear: function () {
            this.props.search.paginate = 1;
            this.props.search.page = 1;
            this.props.search.name = "";
            this.list();
        },
        siteList: function () {
            this.loading.isActive = true;
            this.$store.dispatch('site/lists').then(res => {
                this.site_default_currency = res.data.data.site_default_currency;
                this.loading.isActive = false;
            }).catch((err) => {
                this.loading.isActive = false;
            });

        },
        list: function (page = 1) {
            this.loading.isActive = true;
            this.props.search.page = page;
            return this.$store.dispatch("currency/lists", this.props.search).then((res) => {
                this.loading.isActive = false;
            }).catch((err) => {
                this.loading.isActive = false;
            });
        },
        edit: function (tax) {
            appService.modalShow();
            this.loading.isActive = true;
            this.$store.dispatch("currency/edit", tax.id);
            this.props.form = {
                name: tax.name,
                symbol: tax.symbol,
                code: tax.code,
                is_cryptocurrency: tax.is_cryptocurrency,
                exchange_rate: tax.exchange_rate,
            };
            this.loading.isActive = false;
        },
        destroy: function (id) {
            appService
                .destroyConfirmation()
                .then((res) => {
                    try {
                        this.loading.isActive = true;
                        this.$store
                            .dispatch("currency/destroy", {
                                id: id,
                                search: this.props.search,
                            })
                            .then((res) => {
                                this.loading.isActive = false;
                                alertService.successFlip(
                                    null,
                                    this.$t("menu.currencies")
                                );
                            })
                            .catch((err) => {
                                this.loading.isActive = false;
                                alertService.error(err.response.data.message);
                            });
                    } catch (err) {
                        this.loading.isActive = false;
                        alertService.error(err.response.data.message);
                    }
                })
                .catch((err) => {
                    this.loading.isActive = false;
                });
        },
    },
};
</script>
